/*
 * iter447 — Forecast Revenue Variance Report page.
 *
 * Compares any two calendar months of forecasted revenue, attributes
 * the delta to standard FP&A variance drivers (VOLUME, PRICE,
 * NEW_SITE, DROPPED_SITE) and cross-refs each site's expected audit
 * frequency so the reader can spot whether the variance is aligned
 * with contractual cadence.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Minus, Loader2, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import api from '../services/api';
import { clientsService, sitesService, auditorsService } from '../services';
import RegionFilter from '../components/RegionFilter';
import { useAuth } from '../contexts/AuthContext';

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const DRIVER_META = {
  VOLUME: { label: 'Scope', color: 'bg-amber-100 text-amber-800' },
  PRICE: { label: 'Price', color: 'bg-sky-100 text-sky-800' },
  NEW_SITE: { label: 'New Site', color: 'bg-emerald-100 text-emerald-800' },
  DROPPED_SITE: { label: 'Dropped', color: 'bg-rose-100 text-rose-800' },
  RESCHEDULED: { label: 'Rescheduled', color: 'bg-slate-200 text-slate-800' },
};

const fmt = (v) => `£${Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`;
const fmtPct = (v) => v == null ? '—' : `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;

export default function ForecastVarianceReportPage() {
  const navigate = useNavigate();
  const { isAdmin, isScheduler } = useAuth();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  // Baseline defaults to CURRENT month, comparison to NEXT month
  const [baselineYear, setBaselineYear] = useState(currentYear);
  const [baselineMonth, setBaselineMonth] = useState(currentMonth);
  const [comparisonYear, setComparisonYear] = useState(
    currentMonth === 12 ? currentYear + 1 : currentYear
  );
  const [comparisonMonth, setComparisonMonth] = useState(
    currentMonth === 12 ? 1 : currentMonth + 1
  );
  const [groupBy, setGroupBy] = useState('client');

  // iter458 — Filters (regions, auditors, customers, sites)
  const [regionId, setRegionId] = useState(null);
  const [auditorId, setAuditorId] = useState('');
  const [clientId, setClientId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [auditors, setAuditors] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [includeDraftJobs, setIncludeDraftJobs] = useState(false);

  // iter447c — Drill-down modal state. Opened by clicking any (group|site, period)
  // amount cell to reveal the individual jobs feeding that value.
  const [drilldown, setDrilldown] = useState(null); // { title, scope, period, year, month, data, loading }

  // iter458 — Load reference data (customers, sites, auditors) for filters
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, s, a] = await Promise.all([
          clientsService.list({ is_active: true }).catch(() => []),
          sitesService.list({ is_active: true }).catch(() => []),
          auditorsService.list({ is_active: true }).catch(() => []),
        ]);
        if (cancelled) return;
        setCustomers(Array.isArray(c) ? c : (c?.clients || c?.items || []));
        setSites(Array.isArray(s) ? s : (s?.sites || s?.items || []));
        const audList = Array.isArray(a) ? a : (a?.auditors || a?.items || []);
        setAuditors([...audList].sort((x, y) =>
          (x.first_name || '').localeCompare(y.first_name || '') ||
          (x.last_name || '').localeCompare(y.last_name || '')
        ));
      } catch (e) {
        console.error('Failed to load filter reference data:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter458 — When a client (group) is selected, narrow the site
  // dropdown to sites under that client only.
  const visibleSites = useMemo(() => {
    if (!clientId) return sites;
    const cid = parseInt(clientId, 10);
    return sites.filter((s) =>
      s.parent_client_id === cid || s.independent_client_id === cid
    );
  }, [sites, clientId]);

  // Reset site if it no longer belongs to the selected client
  useEffect(() => {
    if (siteId && !visibleSites.some((s) => String(s.id) === String(siteId))) {
      setSiteId('');
    }
  }, [visibleSites, siteId]);

  // iter458 — Date-range presets: apply relative to today.
  // iter459 — "next_vs_this" now sets baseline=Next month, comparison=This
  // month so the report title reads "Next month → This month" and the
  // variance convention (base − comparator) surfaces growth as positive
  // when Next > This.
  const applyPreset = useCallback((preset) => {
    const y = today.getFullYear();
    const m = today.getMonth() + 1;
    const prev = m === 1 ? { y: y - 1, m: 12 } : { y, m: m - 1 };
    const next = m === 12 ? { y: y + 1, m: 1 } : { y, m: m + 1 };
    switch (preset) {
      case 'last_vs_this':
        setBaselineYear(prev.y); setBaselineMonth(prev.m);
        setComparisonYear(y); setComparisonMonth(m);
        break;
      case 'next_vs_this':
        setBaselineYear(next.y); setBaselineMonth(next.m);
        setComparisonYear(y); setComparisonMonth(m);
        break;
      case 'last_vs_next':
        setBaselineYear(prev.y); setBaselineMonth(prev.m);
        setComparisonYear(next.y); setComparisonMonth(next.m);
        break;
      default:
        break;
    }
  }, [today]);

  const openDrilldown = async ({ title, period, year, month, scope }) => {
    setDrilldown({ title, period, year, month, scope, data: null, loading: true });
    try {
      const params = new URLSearchParams({
        year: String(year),
        month: String(month),
        ...scope,
      });
      // iter458 — Propagate the report-wide auditor / region filters
      // so the drill-down stays consistent with what the user sees.
      if (auditorId) params.set('filter_auditor_id', String(auditorId));
      if (regionId !== null && regionId !== undefined) params.set('filter_region_id', String(regionId));
      if ((isAdmin || isScheduler) && includeDraftJobs) params.set('include_draft_jobs', 'true');
      const resp = await api.get(`/reports/forecast-variance/jobs?${params.toString()}`);
      setDrilldown((d) => (d ? { ...d, data: resp.data, loading: false } : d));
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to load job details');
      setDrilldown(null);
    }
  };


  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear - 2; y <= currentYear + 2; y++) arr.push(y);
    return arr;
  }, [currentYear]);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        baseline_year: String(baselineYear),
        baseline_month: String(baselineMonth),
        comparison_year: String(comparisonYear),
        comparison_month: String(comparisonMonth),
        group_by: groupBy,
      });
      // iter458 — Filters
      if (regionId !== null && regionId !== undefined) params.set('region_id', String(regionId));
      if (auditorId) params.set('auditor_id', String(auditorId));
      if (clientId) params.set('client_id', String(clientId));
      if (siteId) params.set('site_id', String(siteId));
      if ((isAdmin || isScheduler) && includeDraftJobs) params.set('include_draft_jobs', 'true');
      const resp = await api.get(`/reports/forecast-variance?${params.toString()}`);
      setData(resp.data);
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Failed to load Forecast Variance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // load() intentionally not re-called on every deps change

  const varianceIcon = (v) => {
    if (v == null || v === 0) return <Minus className="w-4 h-4 text-slate-400" />;
    return v > 0
      ? <TrendingUp className="w-4 h-4 text-emerald-600" />
      : <TrendingDown className="w-4 h-4 text-rose-600" />;
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="forecast-variance-page">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')}
                  data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Forecast Revenue Variance</h1>
            <p className="text-sm text-slate-500">
              Compare forecasted revenue between two calendar months, broken down by group and site,
              with variance drivers and audit-frequency overlay.
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <Card>
          <CardContent className="p-4 space-y-4">
            {/* iter458 — Quick date presets */}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-500 mr-1">Quick pick:</span>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => applyPreset('last_vs_this')}
                      data-testid="preset-last-vs-this">
                Last month → This month
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => applyPreset('next_vs_this')}
                      data-testid="preset-next-vs-this">
                Next month → This month
              </Button>
              <Button variant="outline" size="sm" className="h-7 text-xs"
                      onClick={() => applyPreset('last_vs_next')}
                      data-testid="preset-last-vs-next">
                Last month → Next month
              </Button>
            </div>

            {/* Period + group selectors */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <div className="text-xs text-slate-500 mb-1">Baseline period</div>
                <div className="flex gap-2">
                  <Select value={String(baselineMonth)} onValueChange={(v) => setBaselineMonth(parseInt(v))}>
                    <SelectTrigger className="w-[110px]" data-testid="baseline-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                  <Select value={String(baselineYear)} onValueChange={(v) => setBaselineYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]" data-testid="baseline-year"><SelectValue /></SelectTrigger>
                    <SelectContent>{years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Comparison period</div>
                <div className="flex gap-2">
                  <Select value={String(comparisonMonth)} onValueChange={(v) => setComparisonMonth(parseInt(v))}>
                    <SelectTrigger className="w-[110px]" data-testid="comparison-month"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                  <Select value={String(comparisonYear)} onValueChange={(v) => setComparisonYear(parseInt(v))}>
                    <SelectTrigger className="w-[100px]" data-testid="comparison-year"><SelectValue /></SelectTrigger>
                    <SelectContent>{years.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Group by</div>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-[160px]" data-testid="group-by"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client / Group</SelectItem>
                    <SelectItem value="region">Region</SelectItem>
                    <SelectItem value="site">Site (flat)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* iter458 — Filter row: region / customer / site / auditor */}
            <div className="flex flex-wrap gap-4 items-end border-t border-slate-100 pt-3">
              <div>
                <div className="text-xs text-slate-500 mb-1">Region</div>
                <div className="min-h-[36px]">
                  <RegionFilter value={regionId} onChange={setRegionId} />
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Customer</div>
                <Select value={clientId ? String(clientId) : 'all'}
                        onValueChange={(v) => setClientId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[220px]" data-testid="filter-customer"><SelectValue placeholder="All customers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All customers</SelectItem>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Site</div>
                <Select value={siteId ? String(siteId) : 'all'}
                        onValueChange={(v) => setSiteId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[220px]" data-testid="filter-site"><SelectValue placeholder="All sites" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All sites</SelectItem>
                    {visibleSites.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Auditor</div>
                <Select value={auditorId ? String(auditorId) : 'all'}
                        onValueChange={(v) => setAuditorId(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[200px]" data-testid="filter-auditor"><SelectValue placeholder="All auditors" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All auditors</SelectItem>
                    {auditors.map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.first_name} {a.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {(regionId !== null || auditorId || clientId || siteId) && (
                <Button variant="ghost" size="sm" className="h-9 text-xs"
                        onClick={() => { setRegionId(null); setAuditorId(''); setClientId(''); setSiteId(''); }}
                        data-testid="clear-filters">
                  Clear filters
                </Button>
              )}
              {/* Include Draft Jobs — Admin/Scheduler only */}
              {(isAdmin || isScheduler) && (
                <div className="flex items-center gap-1.5 self-end pb-1" data-testid="include-draft-jobs-wrap-fv">
                  <input id="fv-include-draft" type="checkbox"
                    checked={includeDraftJobs}
                    onChange={e => setIncludeDraftJobs(e.target.checked)}
                    className="h-4 w-4 accent-amber-500 cursor-pointer"
                    data-testid="fv-include-draft-checkbox" />
                  <Label htmlFor="fv-include-draft" className="text-xs cursor-pointer select-none whitespace-nowrap">Include draft jobs</Label>
                </div>
              )}
              <div className="flex-1" />
              <Button onClick={load} disabled={loading} data-testid="run-report-btn">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Run Report
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Draft jobs banner */}
        {data && includeDraftJobs && (isAdmin || isScheduler) && (
          <div className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-800" data-testid="fv-draft-jobs-banner">
            <span className="font-semibold">DRAFT JOBS INCLUDED</span>
            <span className="text-amber-700">— Results include jobs from DRAFT, PENDING APPROVAL and RETURNED batches. For planning use only.</span>
          </div>
        )}

        {/* Summary tiles */}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">{data.baseline.label} Forecast</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-semibold">{fmt(data.baseline.total_forecast)}</div>
                <div className="text-xs text-slate-500">{data.baseline.job_count} jobs scheduled</div></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">{data.comparison.label} Forecast</CardTitle></CardHeader>
              <CardContent><div className="text-2xl font-semibold">{fmt(data.comparison.total_forecast)}</div>
                <div className="text-xs text-slate-500">{data.comparison.job_count} jobs scheduled</div></CardContent></Card>
            <Card><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-wider text-slate-500">Net Variance</CardTitle></CardHeader>
              <CardContent><div className={`text-2xl font-semibold flex items-center gap-2 ${data.variance.absolute >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                {varianceIcon(data.variance.absolute)} {fmt(data.variance.absolute)}
              </div>
                <div className="text-xs text-slate-500">
                  {fmtPct(data.variance.percent)} · {data.variance.job_count_delta >= 0 ? '+' : ''}{data.variance.job_count_delta} jobs
                </div></CardContent></Card>
          </div>
        )}

        {/* Variance table by group */}
        {data && (
          <Card>
            <CardHeader><CardTitle className="text-base">Variance breakdown ({data.groups.length} {groupBy === 'client' ? 'clients' : groupBy === 'region' ? 'regions' : 'sites'})</CardTitle></CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm" data-testid="variance-table">
                  <thead className="bg-slate-100 text-xs uppercase text-slate-600">
                    <tr>
                      <th className="text-left px-4 py-2">Group / Site</th>
                      <th className="text-right px-4 py-2">{data.baseline.label}</th>
                      <th className="text-right px-4 py-2">{data.comparison.label}</th>
                      <th className="text-right px-4 py-2">Variance £</th>
                      <th className="text-right px-4 py-2">Variance %</th>
                      <th className="text-left px-4 py-2">Drivers</th>
                      <th className="text-left px-4 py-2">Audit Frequency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groups.map((g) => {
                      const expanded = expandedGroups[g.group_key];
                      return (
                        <React.Fragment key={g.group_key}>
                          <tr className="bg-slate-50 border-t border-slate-200 hover:bg-slate-100 cursor-pointer"
                              onClick={() => setExpandedGroups((prev) => ({ ...prev, [g.group_key]: !prev[g.group_key] }))}
                              data-testid={`group-row-${g.group_key}`}>
                            <td className="px-4 py-2 font-medium">
                              {expanded ? '▾' : '▸'} {g.group_label}
                              <span className="text-slate-500 text-xs ml-2">({g.sites.length} site{g.sites.length === 1 ? '' : 's'})</span>
                            </td>
                            <td className="text-right px-4 py-2 text-slate-800">{fmt(g.baseline_forecast)}</td>
                            <td className="text-right px-4 py-2 text-slate-800">{fmt(g.comparison_forecast)}</td>
                            <td className={`text-right px-4 py-2 font-medium ${g.variance_absolute >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(g.variance_absolute)}</td>
                            <td className={`text-right px-4 py-2 ${g.variance_absolute >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtPct(g.variance_percent)}</td>
                            <td className="px-4 py-2 text-slate-400 text-xs">—</td>
                            <td className="px-4 py-2 text-slate-400 text-xs">—</td>
                          </tr>
                          {expanded && g.sites.map((s) => (
                            <tr key={s.site_id} className="border-t border-slate-100"
                                data-testid={`site-row-${s.site_id}`}>
                              <td className="px-4 py-2 pl-10 text-slate-700">{s.site_name}
                                <div className="text-xs text-slate-400">
                                  {s.baseline_job_count} → {s.comparison_job_count} jobs
                                </div>
                              </td>
                              <td className="text-right px-4 py-2 text-slate-600 cursor-pointer hover:underline"
                                  onClick={() => openDrilldown({ title: s.site_name, period: data.baseline.label, year: data.baseline.year, month: data.baseline.month, scope: { site_id: s.site_id } })}
                                  data-testid={`site-baseline-${s.site_id}`}>{fmt(s.baseline_forecast)}</td>
                              <td className="text-right px-4 py-2 text-slate-600 cursor-pointer hover:underline"
                                  onClick={() => openDrilldown({ title: s.site_name, period: data.comparison.label, year: data.comparison.year, month: data.comparison.month, scope: { site_id: s.site_id } })}
                                  data-testid={`site-comparison-${s.site_id}`}>{fmt(s.comparison_forecast)}</td>
                              <td className={`text-right px-4 py-2 ${s.variance_absolute >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmt(s.variance_absolute)}</td>
                              <td className={`text-right px-4 py-2 ${s.variance_absolute >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{fmtPct(s.variance_percent)}</td>
                              <td className="px-4 py-2">
                                <div className="flex flex-wrap gap-1">
                                  {s.drivers.map((d, i) => {
                                    const meta = DRIVER_META[d.kind] || DRIVER_META.VOLUME;
                                    return (
                                      <Badge key={i} className={`${meta.color} text-[10px] font-normal`}
                                             title={d.explanation}>
                                        {meta.label} {d.amount >= 0 ? '+' : ''}{fmt(d.amount)}
                                      </Badge>
                                    );
                                  })}
                                  {s.drivers.length === 0 && <span className="text-xs text-slate-400">No change</span>}
                                </div>
                              </td>
                              <td className="px-4 py-2 text-xs">
                                {s.expected_interval_days ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className="text-slate-500">Every {s.expected_interval_days}d</span>
                                    <div className="flex gap-1">
                                      <Badge className={`text-[10px] font-normal ${s.baseline_within_frequency ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                                        {data.baseline.label}: {s.baseline_within_frequency ? 'On' : 'Off'}
                                      </Badge>
                                      <Badge className={`text-[10px] font-normal ${s.comparison_within_frequency ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>
                                        {data.comparison.label}: {s.comparison_within_frequency ? 'On' : 'Off'}
                                      </Badge>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">ad-hoc</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
        {/* iter447c — Drill-down modal: shows individual jobs behind a period cell */}
        <Dialog open={!!drilldown} onOpenChange={(open) => { if (!open) setDrilldown(null); }}>
          <DialogContent
            className="overflow-hidden flex flex-col dialog-resizable"
            style={{ width: '56rem', maxHeight: '85vh' }}
            data-testid="drilldown-dialog"
          >
            <DialogHeader>
              <DialogTitle className="text-lg" data-testid="drilldown-title">
                {drilldown?.title || 'Jobs'} — {drilldown?.period || ''}
              </DialogTitle>
              <DialogDescription>
                {drilldown?.loading ? (
                  <span className="flex items-center gap-2 text-xs"><Loader2 className="w-3 h-3 animate-spin" /> Loading jobs…</span>
                ) : drilldown?.data ? (
                  <span data-testid="drilldown-summary">
                    {drilldown.data.job_count} job{drilldown.data.job_count === 1 ? '' : 's'} · Total {fmt(drilldown.data.total_price)}
                  </span>
                ) : null}
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-auto flex-1 -mx-6 px-6">
              {drilldown?.loading && (
                <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              )}
              {drilldown?.data && drilldown.data.jobs.length === 0 && (
                <div className="py-8 text-center text-sm text-slate-500">No jobs scheduled in this period.</div>
              )}
              {drilldown?.data && drilldown.data.jobs.length > 0 && (
                <table className="min-w-full text-sm" data-testid="drilldown-jobs-table">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Site</th>
                      <th className="text-left px-3 py-2">Service(s)</th>
                      <th className="text-left px-3 py-2">Auditor(s)</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-right px-3 py-2">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldown.data.jobs.map((j) => (
                      <tr key={j.job_id} className="border-t border-slate-100" data-testid={`drilldown-job-${j.job_id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                          {j.scheduled_date}
                          {j.end_date && j.end_date !== j.scheduled_date && (
                            <span className="text-slate-400"> → {j.end_date}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-slate-800">{j.site_name || '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {j.services.length === 0 && <span className="text-slate-400">—</span>}
                            {j.services.map((s, i) => (
                              <Badge key={i} className="bg-slate-100 text-slate-700 text-[10px] font-normal">{s}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-slate-700">
                          {j.auditors.length > 0 ? (
                            <span className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-slate-400" />{j.auditors.join(', ')}</span>
                          ) : (
                            <span className="text-slate-400">Unassigned</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <Badge className={`text-[10px] font-normal ${
                            j.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                            j.status === 'cancelled' ? 'bg-rose-100 text-rose-800' :
                            j.status === 'in_progress' ? 'bg-sky-100 text-sky-800' :
                            'bg-slate-100 text-slate-700'
                          }`}>{(j.status || '').replace('_', ' ')}</Badge>
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-800">{fmt(j.price)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200 sticky bottom-0">
                    <tr>
                      <td className="px-3 py-2 font-medium text-slate-700" colSpan={5}>Total</td>
                      <td className="px-3 py-2 text-right font-semibold" data-testid="drilldown-total">{fmt(drilldown.data.total_price)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
